import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, UploadCloud, Sparkles, Image as ImageIcon, X } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { AutocompleteInput } from './AutocompleteInput';
import { SPORTS, TOURNAMENT_TYPES, bannerUrl, FIELD_LABELS } from './tournamentHubUtils';
import { API } from '../../utils/apiConfig';

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm text-slate-600">{label}</Label>
    {children}
  </div>
);

function BannerReviewPanel({ extracted, formData, onApply, onDismiss }) {
  const [checked, setChecked] = useState(() => {
    const init = {};
    Object.keys(extracted).forEach((k) => {
      if (FIELD_LABELS[k] && extracted[k]) init[k] = !formData[k];
    });
    return init;
  });
  const keys = Object.keys(extracted).filter((k) => FIELD_LABELS[k] && extracted[k]);
  if (keys.length === 0) return null;

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="banner-review-panel">
      <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
        <Sparkles className="w-4 h-4" /> AI found these details on your banner — review before applying
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {keys.map((k) => (
          <label key={k} className="flex items-start gap-2 text-sm bg-white rounded-lg p-2 border border-amber-200" data-testid={`banner-review-field-${k}`}>
            <Checkbox
              checked={!!checked[k]}
              onCheckedChange={(v) => setChecked((c) => ({ ...c, [k]: !!v }))}
            />
            <span>
              <span className="font-medium text-slate-700">{FIELD_LABELS[k]}: </span>
              <span className="text-slate-500">{String(extracted[k]).slice(0, 80)}</span>
              {formData[k] ? <span className="block text-xs text-red-500">Will overwrite existing: "{String(formData[k]).slice(0, 40)}"</span> : null}
            </span>
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => onApply(checked)} data-testid="apply-banner-fields-btn">
          Apply Selected Fields
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onDismiss} data-testid="dismiss-banner-review-btn">
          Dismiss
        </Button>
      </div>
    </div>
  );
}

export default function TournamentHubForm({ formData, setFormData }) {
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState(null);

  const set = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/tournament-hub/extract-banner`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((f) => ({ ...f, banner_file_id: res.data.file_id, banner_url: res.data.banner_url }));
      if (res.data.extracted && Object.keys(res.data.extracted).length > 0) {
        setExtracted(res.data.extracted);
        toast.success('Banner uploaded — review the AI-extracted fields below');
      } else {
        toast.success('Banner uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Banner upload failed');
    } finally {
      setUploading(false);
    }
  };

  const applyExtracted = (checked) => {
    setFormData((f) => {
      const next = { ...f };
      Object.keys(checked).forEach((k) => { if (checked[k]) next[k] = extracted[k]; });
      return next;
    });
    setExtracted(null);
    toast.success('Fields applied — please review before saving');
  };

  return (
    <div className="space-y-4" data-testid="tournament-hub-form">
      {/* Banner upload (drives auto-fill) */}
      <div className="space-y-2">
        <Label className="text-sm text-slate-600 flex items-center gap-1">
          <ImageIcon className="w-4 h-4" /> Tournament Banner (optional — auto-fills details below)
        </Label>
        <div className="flex items-center gap-3">
          {formData.banner_file_id ? (
            <div className="relative">
              <img src={bannerUrl(formData.banner_file_id)} alt="Banner" className="h-20 w-32 object-cover rounded-lg border border-slate-200" />
              <button
                type="button"
                onClick={() => { set('banner_file_id', ''); set('banner_url', ''); }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                data-testid="remove-banner-btn"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-4 py-3 cursor-pointer hover:border-amber-400 text-sm text-slate-500" data-testid="banner-upload-dropzone">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {uploading ? 'Uploading & analysing...' : 'Upload Banner Image'}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleBannerUpload} disabled={uploading} data-testid="banner-file-input" />
            </label>
          )}
        </div>
      </div>

      {extracted && (
        <BannerReviewPanel extracted={extracted} formData={formData} onApply={applyExtracted} onDismiss={() => setExtracted(null)} />
      )}

      <Accordion type="multiple" defaultValue={['details', 'organiser', 'sponsors']} className="space-y-2">
        <AccordionItem value="details" className="border border-slate-200 rounded-lg px-4">
          <AccordionTrigger data-testid="section-tournament-details">1. Tournament Details</AccordionTrigger>
          <AccordionContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tournament Name *">
                <Input value={formData.name} onChange={(e) => set('name', e.target.value)} placeholder="MAKS Open Pickleball Championship" data-testid="input-tournament-name" />
              </Field>
              <Field label="Tournament Type">
                <Select value={formData.tournament_type} onValueChange={(v) => set('tournament_type', v)}>
                  <SelectTrigger data-testid="select-tournament-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TOURNAMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Tournament Date *">
                <Input type="date" value={formData.tournament_date} onChange={(e) => set('tournament_date', e.target.value)} data-testid="input-tournament-date" />
              </Field>
              <Field label="Sport">
                <Select value={formData.sport} onValueChange={(v) => set('sport', v)}>
                  <SelectTrigger data-testid="select-sport"><SelectValue /></SelectTrigger>
                  <SelectContent>{SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Venue">
                <AutocompleteInput field="venue" value={formData.venue} onChange={(v) => set('venue', v)} placeholder="Search or enter venue" testId="input-venue" />
              </Field>
              <Field label="Prize Pool (₹)">
                <Input type="number" value={formData.prize_pool} onChange={(e) => set('prize_pool', e.target.value)} placeholder="10000" data-testid="input-prize-pool" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Details">
                <Textarea rows={3} value={formData.details} onChange={(e) => set('details', e.target.value)} placeholder="Tournament format, rules, schedule..." data-testid="input-details" />
              </Field>
              <Field label="Categories">
                <Textarea rows={3} value={formData.categories} onChange={(e) => set('categories', e.target.value)} placeholder="Men's Singles, Women's Doubles, Mixed..." data-testid="input-categories" />
              </Field>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="organiser" className="border border-slate-200 rounded-lg px-4">
          <AccordionTrigger data-testid="section-organiser-details">2. Organiser Details</AccordionTrigger>
          <AccordionContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Organiser Name"><AutocompleteInput field="organiser_name" value={formData.organiser_name} onChange={(v) => set('organiser_name', v)} testId="input-organiser-name" /></Field>
              <Field label="Organisation Name"><AutocompleteInput field="organisation_name" value={formData.organisation_name} onChange={(v) => set('organisation_name', v)} testId="input-organisation-name" /></Field>
              <Field label="Contact Person"><AutocompleteInput field="contact_person" value={formData.contact_person} onChange={(v) => set('contact_person', v)} testId="input-contact-person" /></Field>
              <Field label="Mobile Number"><Input type="tel" value={formData.mobile_number} onChange={(e) => set('mobile_number', e.target.value)} data-testid="input-mobile-number" /></Field>
              <Field label="Email"><Input type="email" value={formData.email} onChange={(e) => set('email', e.target.value)} data-testid="input-email" /></Field>
              <Field label="Website / Social Media"><Input value={formData.website} onChange={(e) => set('website', e.target.value)} placeholder="https://..." data-testid="input-website" /></Field>
              <Field label="Organised & Managed By"><AutocompleteInput field="organised_managed_by" value={formData.organised_managed_by} onChange={(v) => set('organised_managed_by', v)} testId="input-organised-managed-by" /></Field>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="sponsors" className="border border-slate-200 rounded-lg px-4">
          <AccordionTrigger data-testid="section-sponsors">3. Partners & Sponsors</AccordionTrigger>
          <AccordionContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Main Sponsor"><Input value={formData.main_sponsor} onChange={(e) => set('main_sponsor', e.target.value)} data-testid="input-main-sponsor" /></Field>
              <Field label="Registration Partner"><Input value={formData.registration_partner} onChange={(e) => set('registration_partner', e.target.value)} data-testid="input-registration-partner" /></Field>
              <Field label="Official Venue Partner"><Input value={formData.official_venue_partner} onChange={(e) => set('official_venue_partner', e.target.value)} data-testid="input-official-venue-partner" /></Field>
              <Field label="Other Sponsors / Partners"><Input value={formData.other_sponsors} onChange={(e) => set('other_sponsors', e.target.value)} data-testid="input-other-sponsors" /></Field>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
